import {
  NETWORK_PREFIX,
  cidrContains,
  firstBlock,
  isPrivateCidr,
  parseIpv4Cidr,
  type Network,
} from "@cloudserver/contracts";
import { Link, useNavigate } from "@tanstack/react-router";
import { ExternalLink, Network as NetworkIcon, Plus, Trash2 } from "lucide-react";
import { useState, type FormEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useCanWrite, useReadOnly } from "../api/users.js";
import {
  progressOf,
  useCreateNetwork,
  useDeleteNetwork,
  useNetworks,
  useNetworkZones,
  useRunningOperations,
} from "../api/networks.js";
import { DangerDialog } from "../ui/confirm.js";
import { ErrorMessage, fieldError, problemText } from "../ui/feedback.js";
import { relativeTime } from "../ui/format.js";
import { FieldHint } from "../ui/help.js";
import { EmptyState, PageHeader } from "../ui/layout.js";
import { Dialog } from "../ui/overlay.js";
import { Alert, Badge, Button, Select, TextField } from "../ui/primitives.js";
import { DataTable } from "../ui/table.js";
import { useToast } from "../ui/toast.js";
import { ApiProblem } from "../api/client.js";
import type { TFunction } from "i18next";

const DEFAULT_RANGE = "10.0.0.0/16";
const DEFAULT_ZONE = "eu-central";

/** What is wrong with a network range, as a translation key; null when it is fine. */
export function rangeProblem(range: string): string | null {
  const cidr = parseIpv4Cidr(range);
  if (!cidr) return "networks.rangeInvalid";
  if (!isPrivateCidr(cidr) || cidr.prefix < NETWORK_PREFIX.min || cidr.prefix > NETWORK_PREFIX.max)
    return "networks.rangeNotPrivate";
  return null;
}

/** What is wrong with a subnet of `range`, as a translation key; null when it is fine. */
export function subnetProblem(range: string, subnet: string): string | null {
  const inner = parseIpv4Cidr(subnet);
  if (!inner) return "networks.subnetInvalid";
  const outer = parseIpv4Cidr(range);
  if (outer && !cidrContains(outer, inner)) return "networks.subnetOutside";
  return null;
}

/** The default first subnet: a /24 at the start of the range (or the range itself when smaller). */
const defaultSubnet = (range: string) => {
  const cidr = parseIpv4Cidr(range);
  return cidr ? (firstBlock(range, Math.max(24, cidr.prefix)) ?? "") : "";
};

/** A labelled native select for forms (zones, servers). */
export function SelectField({
  label,
  hint,
  info,
  value,
  onChange,
  children,
  name,
}: {
  label: string;
  hint?: string;
  info?: ReactNode;
  value: string;
  onChange: (value: string) => void;
  children: ReactNode;
  name: string;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="flex items-center gap-1.5">
        <span className="text-sm font-medium" id={`${name}-label`}>
          {label}
        </span>
        {info}
      </span>
      <Select
        label={label}
        name={name}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="min-h-10 w-full"
      >
        {children}
      </Select>
      {hint && <p className="text-xs text-muted">{hint}</p>}
    </div>
  );
}

function CreateNetworkDialog({
  projectId,
  open,
  onOpenChange,
}: {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const create = useCreateNetwork(projectId);
  const zones = useNetworkZones(projectId);
  const [name, setName] = useState("");
  const [range, setRange] = useState(DEFAULT_RANGE);
  const [subnet, setSubnet] = useState(defaultSubnet(DEFAULT_RANGE));
  const [subnetTouched, setSubnetTouched] = useState(false);
  const [zone, setZone] = useState("");
  const [tried, setTried] = useState(false);
  const zoneCodes = zones.data?.map((z) => z.code) ?? [];
  const chosenZone =
    zone || (zoneCodes.includes(DEFAULT_ZONE) ? DEFAULT_ZONE : (zoneCodes[0] ?? DEFAULT_ZONE));

  const close = (next: boolean) => {
    if (!next) {
      setName("");
      setRange(DEFAULT_RANGE);
      setSubnet(defaultSubnet(DEFAULT_RANGE));
      setSubnetTouched(false);
      setZone("");
      setTried(false);
      create.reset();
    }
    onOpenChange(next);
  };
  const rangeError = rangeProblem(range);
  const subnetError = subnetProblem(range, subnet);
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setTried(true);
    if (rangeError || subnetError) return;
    create.mutate(
      { name: name.trim(), ipRange: range.trim(), zone: chosenZone, subnetIpRange: subnet.trim() },
      {
        onSuccess: (network) => {
          toast({ tone: "ok", title: t("networks.created", { name: network.name }) });
          close(false);
          void navigate({
            to: "/projects/$projectId/networks/$networkId",
            params: { projectId, networkId: network.id },
          });
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={close}
      title={t("networks.create")}
      description={t("networks.createLead")}
      footer={
        <>
          <Button variant="secondary" onClick={() => close(false)}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" form="create-network" busy={create.isPending}>
            {t("networks.submit")}
          </Button>
        </>
      }
    >
      <form id="create-network" onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <TextField
          label={t("networks.name")}
          name="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          maxLength={128}
          hint={t("networks.nameHint")}
          error={fieldError(create.error, "name", t)}
        />
        <TextField
          label={t("networks.ipRange")}
          name="ipRange"
          value={range}
          onChange={(e) => {
            setRange(e.target.value);
            if (!subnetTouched) setSubnet(defaultSubnet(e.target.value.trim()));
          }}
          spellCheck={false}
          hint={t("networks.ipRangeHint")}
          info={<FieldHint text={t("networks.ipRangeWhy")} topic="networks" />}
          error={
            (tried && rangeError ? t(rangeError) : undefined) ??
            fieldError(create.error, "ipRange", t)
          }
        />
        <SelectField
          label={t("networks.zone")}
          name="zone"
          value={chosenZone}
          onChange={setZone}
          hint={t("networks.zoneHint")}
          info={<FieldHint text={t("networks.zoneWhy")} topic="networks" />}
        >
          {(zoneCodes.length ? zoneCodes : [DEFAULT_ZONE]).map((code) => (
            <option key={code} value={code}>
              {code}
            </option>
          ))}
        </SelectField>
        <TextField
          label={t("networks.subnet")}
          name="subnet"
          value={subnet}
          onChange={(e) => {
            setSubnet(e.target.value);
            setSubnetTouched(true);
          }}
          spellCheck={false}
          hint={t("networks.subnetHint")}
          error={
            (tried && subnetError ? t(subnetError) : undefined) ??
            fieldError(create.error, "subnetIpRange", t)
          }
        />
        <ErrorMessage error={create.error} />
      </form>
    </Dialog>
  );
}

/** The toast after a failed write: the translated reason of the problem. */
export const failureToast = (t: TFunction, error: unknown) => ({
  tone: "danger" as const,
  title:
    error instanceof ApiProblem && error.problem
      ? problemText(t, error.problem)
      : t("common.unexpected"),
});

export function NetworksPage({ projectId }: { projectId: string }) {
  const { t, i18n } = useTranslation();
  // Guests may only look (docs/users.md): no create action; the API refuses them anyway.
  const canWrite = useCanWrite(projectId);
  const networks = useNetworks(projectId);
  const running = useRunningOperations(projectId);
  const remove = useDeleteNetwork(projectId);
  const toast = useToast();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<Network | null>(null);
  const { readOnly, reason: readOnlyReason, token: tokenReadOnly } = useReadOnly(projectId);

  const createButton = (
    <Button
      onClick={() => setCreating(true)}
      disabled={readOnly}
      icon={<Plus className="size-4" aria-hidden />}
    >
      {t("networks.create")}
    </Button>
  );
  const open = (n: Network) =>
    void navigate({
      to: "/projects/$projectId/networks/$networkId",
      params: { projectId, networkId: n.id },
    });

  return (
    <>
      <PageHeader
        title={t("nav.networks")}
        intro={t("networks.intro")}
        help="networks"
        actions={canWrite && networks.data?.length ? createButton : undefined}
      />
      {tokenReadOnly && <Alert tone="warn">{readOnlyReason}</Alert>}
      {networks.error ? (
        <ErrorMessage error={networks.error} />
      ) : (
        <DataTable<Network>
          label={t("nav.networks")}
          rows={networks.data}
          rowKey={(n) => n.id}
          initialSort={{ key: "name", dir: "asc" }}
          progress={(n) => progressOf(running.data, n.id)}
          empty={
            <EmptyState
              icon={<NetworkIcon className="size-6" aria-hidden />}
              title={t("networks.empty")}
              action={canWrite ? createButton : undefined}
              help="networks"
            >
              {t("networks.emptyLead")}
            </EmptyState>
          }
          columns={[
            {
              key: "name",
              header: t("networks.name"),
              primary: true,
              sort: (n) => n.name.toLowerCase(),
              cell: (n) => (
                <span className="flex flex-wrap items-center gap-2">
                  <Link
                    to="/projects/$projectId/networks/$networkId"
                    params={{ projectId, networkId: n.id }}
                    className="font-medium text-accent-strong hover:underline"
                  >
                    {n.name}
                  </Link>
                  {n.protection.delete && <Badge>{t("networks.protected")}</Badge>}
                </span>
              ),
            },
            {
              key: "range",
              header: t("networks.ipRange"),
              sort: (n) => n.ipRange,
              cell: (n) => <span className="font-mono text-xs">{n.ipRange}</span>,
            },
            {
              key: "subnets",
              header: t("networks.subnets"),
              align: "right",
              sort: (n) => n.subnets.length,
              cell: (n) => n.subnets.length,
            },
            {
              key: "servers",
              header: t("networks.servers"),
              align: "right",
              sort: (n) => n.servers.length,
              cell: (n) => n.servers.length,
            },
            {
              key: "created",
              header: t("networks.createdOn"),
              hideOnPhone: true,
              sort: (n) => n.createdAt,
              cell: (n) => (
                <span className="text-muted">{relativeTime(n.createdAt, i18n.language)}</span>
              ),
            },
          ]}
          rowMenu={(n) => [
            [
              {
                label: t("networks.open"),
                icon: <ExternalLink className="size-4" aria-hidden />,
                onSelect: () => open(n),
              },
            ],
            [
              {
                label: t("networks.delete"),
                icon: <Trash2 className="size-4" aria-hidden />,
                danger: true,
                disabled: readOnly || n.protection.delete,
                reason: readOnly ? readOnlyReason : t("networks.protectedReason"),
                onSelect: () => setDeleting(n),
              },
            ],
          ]}
        />
      )}
      <CreateNetworkDialog projectId={projectId} open={creating} onOpenChange={setCreating} />
      <DangerDialog
        open={deleting !== null}
        onOpenChange={(next) => !next && setDeleting(null)}
        title={t("networks.deleteTitle", { name: deleting?.name ?? "" })}
        consequences={deleteConsequences(t, deleting)}
        confirmText={deleting?.name ?? ""}
        actionLabel={t("networks.delete")}
        busy={remove.isPending}
        onConfirm={() =>
          deleting &&
          remove.mutate(deleting.id, {
            onSuccess: () => {
              toast({ tone: "ok", title: t("networks.deleted", { name: deleting.name }) });
              setDeleting(null);
            },
            onError: (error) => toast(failureToast(t, error)),
          })
        }
      />
    </>
  );
}

/** What deleting a network does, shown before it is confirmed. */
export function deleteConsequences(t: TFunction, network: Network | null): string[] {
  if (!network) return [];
  return [
    ...(network.servers.length > 0
      ? [t("networks.deleteServers", { count: network.servers.length })]
      : []),
    t("networks.deleteSubnetsRoutes"),
    t("networks.deletePublic"),
  ];
}
